/**
 * 数据处理器 - 简化版
 * 处理音频和BlendShapes数据，管理Player
 */

import { Player } from './Player.js';
import latencyTrace from '../../utils/latencyTrace.js';

const DIAG_PREFIX = '[录课数字人诊断]';

export class Processor {
  constructor() {
    this.player = null;
    this.blendshapesData = []; // 存储完整的BlendShapes数据（累积所有段）
    this.blendshapesNames = [];
    this.blendshapesSampleRate = 25; // LAM输出帧率
    this.currentFrameIndex = 0;
    this.segmentCount = 0; // 收到的音频段数
    this.totalAudioDuration = 0; // 总音频时长
    this.currentTimeOffset = 0; // 当前累积的时间偏移
    this.currentConversationId = null; // 🆔 当前对话ID
    this.pendingAudioSegments = []; // ⏸️ 暂存音频段，等待 BlendShapes 数据
    this.hasBlendShapesData = false; // ✅ 是否已收到 BlendShapes 数据
    this.playbackStartWaiters = [];
    this.playbackEndWaiters = [];
    // 🚫 被中断的 conversation_id 集合：interrupt() 后到达的同 id 段一律丢弃，
    //    避免「停了 0.5s 又自己继续讲」（后端 TTS 仍在推后续段）
    this.abandonedConversationIds = new Set();
    this.muted = false;
  }

  setMuted(muted) {
    this.muted = !!muted;
    this.player?.setMuted?.(this.muted);
  }

  waitForPlaybackStart(timeoutMs = 10000) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        console.warn(`${DIAG_PREFIX} 等待播放开始超时`, {
          timeoutMs,
          currentConversationId: this.currentConversationId,
          pendingAudioSegments: this.pendingAudioSegments.length,
          hasBlendShapesData: this.hasBlendShapesData,
          hasPlayer: !!this.player,
        });
        resolve();
      }, timeoutMs);
      this.playbackStartWaiters.push(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  resolvePlaybackStart() {
    const waiters = this.playbackStartWaiters.splice(0);
    waiters.forEach((resolve) => resolve());
  }

  waitForPlaybackEnd(timeoutMs = 60000) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        console.warn(`${DIAG_PREFIX} 等待播放结束超时`, {
          timeoutMs,
          currentConversationId: this.currentConversationId,
          pendingAudioSegments: this.pendingAudioSegments.length,
          hasBlendShapesData: this.hasBlendShapesData,
          hasPlayer: !!this.player,
          segmentCount: this.segmentCount,
          totalAudioDuration: this.totalAudioDuration,
        });
        resolve();
      }, timeoutMs);
      this.playbackEndWaiters.push(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  resolvePlaybackEnd() {
    const waiters = this.playbackEndWaiters.splice(0);
    waiters.forEach((resolve) => resolve());
  }
  
  /**
   * 处理音频消息
   * @param {Object} audioMessage - {type: 'audio', conversation_id, data, sample_rate, duration}
   */
  processAudio(audioMessage) {
    const { conversation_id, segment_index, total_segments, data, sample_rate, duration } = audioMessage;

    // 🚫 已被打断的对话：丢弃所有后续段（含本段），避免续播
    if (this.abandonedConversationIds.has(conversation_id)) {
      console.log(`🚫 丢弃被打断对话的音频段: ${String(conversation_id).slice(0, 8)}… seg=${segment_index}`);
      return;
    }
    // 🚫 静音窗口内到达的新对话：也加入 abandoned 并丢弃
    if (this.muteUntilTs && Date.now() < this.muteUntilTs &&
        this.currentConversationId !== conversation_id) {
      this.abandonedConversationIds.add(conversation_id);
      console.log(`🚫 静音窗口内丢弃新对话: ${String(conversation_id).slice(0, 8)}…`);
      return;
    }

    // 🆔 检测新对话：conversation_id 改变
    const isNewConversation = this.currentConversationId !== conversation_id;
    
    if (isNewConversation) {
      console.log(`🎬 检测到新对话: ${conversation_id} (旧ID: ${this.currentConversationId})`);
      
      // 清理旧的 Player
      if (this.player) {
        console.log('🔄 清理旧 Player');
        this.resolvePlaybackEnd();
        this.player.destroy();
      }
      
      // 重置所有状态
      this.currentConversationId = conversation_id;
      this.segmentCount = 0;
      this.totalAudioDuration = 0;
      this.currentTimeOffset = 0;
      this.blendshapesData = [];
      this.pendingAudioSegments = []; // ✅ 重置暂存队列
      this.hasBlendShapesData = false; // ✅ 重置同步标志
      
      // 创建新的 Player
      this.player = new Player({
        channels: 1,
        sampleRate: sample_rate,
        onStartSpeaking: (player) => {
          console.log('🎤 开始说话');
          // 扬声器真正出声：同时终结「发言链路」和「提问链路」两条时间线
          latencyTrace.markSpeak('sound');
          latencyTrace.markTurn('sound');
          this.resolvePlaybackStart();
        },
        onEndSpeaking: (player) => {
          console.log(`✅ 对话 ${conversation_id} 播放结束（共${this.segmentCount}段，总时长${this.totalAudioDuration.toFixed(2)}秒）`);
          this.resolvePlaybackEnd();
          // 立即清理
          this.clear();
        }
      });
      this.player.setMuted(this.muted);
    }
    
    // Base64解码
    const audioData = atob(data);
    const audioBytes = new Uint8Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      audioBytes[i] = audioData.charCodeAt(i);
    }
    
    // 转换为Int16Array
    const audioInt16 = new Int16Array(audioBytes.buffer);
    
    // 统计段数和总时长
    this.segmentCount++;
    this.totalAudioDuration += duration;
    
    // 准备音频段数据
    const audioSegment = {
      audio: audioInt16,
      end_of_batch: Number.isFinite(total_segments) ? segment_index >= total_segments - 1 : true,
      segment_index: segment_index,
      duration: duration
    };
    
    // ⏸️ 如果还没收到 BlendShapes 数据，暂存音频段
    if (!this.hasBlendShapesData) {
      this.pendingAudioSegments.push(audioSegment);
      console.log(`⏸️  暂存音频段${segment_index + 1}/${total_segments}，等待 BlendShapes 数据`);
      return;
    }
    
    // ✅ 已有 BlendShapes 数据，立即播放
    this.player.feed({
      audio: audioSegment.audio,
      end_of_batch: audioSegment.end_of_batch
    });
    
    console.log(`🔊 对话${conversation_id.substring(0, 8)}... 段${segment_index + 1}/${total_segments}: ${duration.toFixed(2)}秒, ${sample_rate}Hz`);
  }
  
  /**
   * 处理BlendShapes消息
   * @param {Object} blendshapesMessage - {type: 'blendshapes', data: {names, frames}}
   */
  processBlendShapes(blendshapesMessage) {
    const { data, conversation_id } = blendshapesMessage;
    // 🚫 已被打断的对话：丢弃 blendshapes，避免被嘴型驱动
    if (conversation_id && this.abandonedConversationIds.has(conversation_id)) {
      return;
    }
    this.blendshapesNames = data.names;
    
    // ✅ 累积 BlendShapes 数据，调整时间偏移
    const newFrames = data.frames.map(frame => ({
      weights: frame.weights,
      time: frame.time + this.currentTimeOffset  // 加上累积的时间偏移
    }));
    
    this.blendshapesData.push(...newFrames);
    
    // 更新时间偏移（为下一段准备）
    if (newFrames.length > 0) {
      const lastFrameTime = newFrames[newFrames.length - 1].time;
      this.currentTimeOffset = lastFrameTime + (1 / this.blendshapesSampleRate); // 加上一帧的时间
    }
    
    console.log(`📊 BlendShapes数据: 新增${data.frames.length}帧, 累积${this.blendshapesData.length}帧, ${this.blendshapesNames.length}通道`);
    
    // ✅ 首次收到 BlendShapes 数据，释放暂存的音频段
    if (!this.hasBlendShapesData && this.pendingAudioSegments.length > 0) {
      this.hasBlendShapesData = true;
      console.log(`✅ 收到 BlendShapes 数据，释放 ${this.pendingAudioSegments.length} 段暂存音频`);
      
      // 按顺序播放所有暂存的音频段
      this.pendingAudioSegments
        .sort((a, b) => a.segment_index - b.segment_index)
        .forEach((segment, index) => {
        this.player.feed({
          audio: segment.audio,
          end_of_batch: segment.end_of_batch
        });
        console.log(`  ▶️  播放暂存段${segment.segment_index + 1}: ${segment.duration.toFixed(2)}秒`);
        });
      
      // 清空暂存队列
      this.pendingAudioSegments = [];
    } else if (!this.hasBlendShapesData) {
      // 标记为已有数据（即使没有暂存音频）
      this.hasBlendShapesData = true;
    }
  }
  
  /**
   * 获取当前时刻的BlendShapes值
   * 根据音频播放时间，插值查询BlendShapes
   * @returns {Object} - {name1: value1, name2: value2, ...}
   */
  getArkitFaceFrame() {
    if (!this.player) {
      // console.log('⚠️ getArkitFaceFrame: player不存在');
      return null;
    }
    
    if (!this.player.firstStartAbsoluteTime) {
      // console.log('⚠️ getArkitFaceFrame: 播放未开始');
      return null;
    }
    
    if (!this.blendshapesData || this.blendshapesData.length === 0) {
      console.log('⚠️ getArkitFaceFrame: blendshapesData为空');
      return null;
    }
    
    // 计算当前播放的时间偏移（秒）
    const offsetTime = (Date.now() - this.player.firstStartAbsoluteTime) / 1000;
    
    // 查找对应的帧索引
    let frameIndex = 0;
    for (let i = 0; i < this.blendshapesData.length; i++) {
      if (this.blendshapesData[i].time <= offsetTime) {
        frameIndex = i;
      } else {
        break;
      }
    }
    
    // 防止越界
    if (frameIndex >= this.blendshapesData.length) {
      frameIndex = this.blendshapesData.length - 1;
    }
    
    const frame = this.blendshapesData[frameIndex];
    if (!frame || !frame.weights) {
      return null;
    }
    
    // 转换为对象格式 {name: value}
    const result = {};
    this.blendshapesNames.forEach((name, index) => {
      result[name] = frame.weights[index] || 0;
    });
    
    return result;
  }
  
  /**
   * 把当前对话标记为「已被打断」：在 clear() 之前调用，
   * 后续到达的同 conversation_id 段会被 processAudio/processBlendShapes 丢弃
   */
  abandonCurrent() {
    if (this.currentConversationId) {
      this.abandonedConversationIds.add(this.currentConversationId);
      // 防泄漏：只保留最近 32 条 abandoned id
      if (this.abandonedConversationIds.size > 32) {
        const it = this.abandonedConversationIds.values();
        for (let i = 0; i < this.abandonedConversationIds.size - 32; i++) {
          this.abandonedConversationIds.delete(it.next().value);
        }
      }
    }
    // 兜底：1.5s 静音窗口，期间到达的"新对话"也按 abandoned 处理
    // 用于 interrupt 时 currentConversationId 还是 null（音频段还没到）的 race
    this.muteUntilTs = Date.now() + 1500;
    this.pendingAudioSegments = [];
  }

  /**
   * 清理资源
   */
  clear() {
    this.resolvePlaybackStart();
    this.resolvePlaybackEnd();
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    this.blendshapesData = [];
    this.currentFrameIndex = 0;
    this.segmentCount = 0;
    this.totalAudioDuration = 0;
    this.currentTimeOffset = 0;
    this.currentConversationId = null; // 🆔 清除对话ID
    this.pendingAudioSegments = [];
    this.hasBlendShapesData = false;
  }
}

